// The two errors a call to the API can end in. Every query and mutation error is one of them, so
// a screen narrows with `instanceof` instead of sniffing strings (audit frontend A.5).
import { z } from "zod";

import type { ErrorCode } from "./generated/model";
import { ErrorBody } from "./generated/zod";

/** A code this build knows, or one the server added after this build: still an ApiError. */
export type ApiErrorCode = ErrorCode | (string & {});

// The generated envelope with `code` widened to any string, so a tab older than the API still
// reads a new code as an ApiError with its status and message.
const ErrorEnvelope = z.object({ error: ErrorBody.extend({ code: z.string() }) });
export type ApiErrorBody = z.output<typeof ErrorEnvelope>["error"];

/** The server answered with the error envelope (proposal 3.2, contracts/errors.json). */
export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Typed per code in contracts/errors.json; not generated into TypeScript yet. */
  readonly details: Record<string, unknown>;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.request_id;
  }
}

/**
 * The request never produced an envelope: `network` (offline, DNS, reset), `http` (a status
 * without the envelope, such as a load balancer's 502 page) or `malformed` (a 2xx body that is
 * not JSON).
 */
export type TransportErrorKind = "network" | "http" | "malformed";

export class TransportError extends Error {
  override readonly name = "TransportError";
  readonly kind: TransportErrorKind;
  readonly status: number | undefined;

  constructor(kind: TransportErrorKind, status?: number, options?: ErrorOptions) {
    super(status === undefined ? `API ${kind} error` : `API ${kind} error, status ${status}`, options);
    this.kind = kind;
    this.status = status;
  }
}

/**
 * The bucket answered an upload's post with an error status (the signed policy refused, or
 * expired): the file did not land. The upload's pending asset is deleted with it, so the next
 * attempt starts over with a new key instead of resending this one (S1).
 */
export class UploadRefusedError extends Error {
  override readonly name = "UploadRefusedError";
  /** The bucket's status. */
  readonly status: number | undefined;

  constructor(status: number | undefined, options?: ErrorOptions) {
    super(`The bucket refused the upload, status ${String(status)}`, options);
    this.status = status;
  }
}

/** Parses an error body; `undefined` when it is not the envelope. */
export function parseErrorEnvelope(text: string): ApiErrorBody | undefined {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return undefined;
  }
  const result = ErrorEnvelope.safeParse(json);
  return result.success ? result.data.error : undefined;
}

export function isApiError(error: unknown, code?: ErrorCode): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/** True when the server no longer accepts this browser's session. */
export function isSessionExpired(error: unknown): boolean {
  return isApiError(error, "UNAUTHENTICATED");
}
